import { useCallback, useEffect, useState } from 'react';
import i18n from '../i18n';

export type Language = 'en' | 'nl';

const STORAGE_KEY = 'language-preference';

/**
 * useLanguage — single source of truth for the active UI language.
 *
 * The hook is now a thin wrapper around i18next. Every consumer subscribes
 * to `i18n.on('languageChanged', …)` so a flip from any toggle propagates
 * to all components that read `language`. Existing inline-ternary call
 * sites (`language === 'en' ? 'X' : 'Y'`) keep working unchanged; new
 * pages use the `useTranslation()` hook from react-i18next directly.
 */
export function useLanguage() {
  const [language, setLanguageState] = useState<Language>(
    (i18n.language as Language) || 'en'
  );

  useEffect(() => {
    const onChange = (lng: string) => {
      if (lng === 'en' || lng === 'nl') setLanguageState(lng);
    };
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    void i18n.changeLanguage(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute('lang', lang);
    // Sync language preference to backend (fire-and-forget). Portal auth is an
    // httpOnly cookie — send it with credentials:'include' (there is no
    // 'portal_token' in localStorage, so the old Bearer guard never synced).
    fetch('/api/portal/auth/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ language: lang }),
    }).catch(() => {}); // Silently ignore errors
  }, []);

  const toggle = useCallback(() => {
    setLanguage(language === 'en' ? 'nl' : 'en');
  }, [language, setLanguage]);

  return { language, setLanguage, toggle };
}
